import { normalizeToEditorContent } from './contentGuards';
import { removeChunkAttributes } from './chunkHelpers';
import { computeContentOffset, computeDocumentStats } from './statHelpers';
import {
  buildVersionWorkerUrl,
  requestWorker
} from '../api/r2';
import { apiCreateImage, apiGetImageByKey, apiGetImagesForDoc, apiLinkImagesToVersion } from '../api/images';
import {
  apiUpsertVersion,
  apiBackfillVersionIfMissing,
  apiDeleteVersion as apiDeleteVersionRow,
  apiGetProtectedVersionIds,
  apiGetVersionsForDoc,
  apiRenameVersion,
  apiSetVersionProtect
} from '../api/versions';
import { debug, isDebugEnabled } from './debug';
import {
  collectImageUrlsFromContent,
  extractImageKeyFromUrl,
  getDocImageKeysFromR2,
  reconcileOrphanImages
} from './imageHelpers';
import config from '../config';

export const MAX_DOC_VERSIONS = (config && config.VERSIONING && config.VERSIONING.MAX_VERSIONS) || 10;

const buildVersionRequestUrl = buildVersionWorkerUrl;

const createVersionIdUtc = (date = new Date()) => {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}_${pad(date.getUTCHours())}-${pad(date.getUTCMinutes())}-${pad(date.getUTCSeconds())}`;
};

const fnv1aHash = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const toNumericIdOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.trunc(numeric);
};

const createVersionContent = ({ projectId, docId, snapshot, versionId, savedAt, trigger, signature, contentOffset = null }) => {

  const fallbackDocument = snapshot?.document && typeof snapshot.document === 'object'
    ? snapshot.document
    : (snapshot?.doc && typeof snapshot.doc === 'object' ? snapshot.doc : {});

  const rawContent =
    fallbackDocument.content ??
    snapshot?.content ??
    snapshot?.doc?.content ??
    null;

  const docJson =
    rawContent && typeof rawContent === 'object' && rawContent.type === 'doc'
      ? rawContent
      : (rawContent?.json && rawContent.json.type === 'doc' ? rawContent.json : null);

  const normalizedDocJson = removeChunkAttributes(docJson);

  return {
    version: {
      id: versionId,
      saved_at: savedAt,
      trigger,
      content_signature: signature || null,
      content_offset: Number.isFinite(Number(contentOffset)) ? Math.trunc(Number(contentOffset)) : null
    },
    document: {
      doc_id: toNumericIdOrNull(docId),
      name: fallbackDocument.name ?? snapshot?.name ?? null,
      layout: fallbackDocument.layout ?? snapshot?.layout ?? null,
      updated_at: fallbackDocument.updated_at ?? snapshot?.updated_at ?? savedAt,
      content: normalizedDocJson
    },
    stats: snapshot?.stats ?? fallbackDocument?.stats ?? null,
    metadata: snapshot?.metadata || {}
  };
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const parseVersionDate = (version) => {
  const direct = version?.savedAt ? new Date(version.savedAt) : null;
  if (direct && !Number.isNaN(direct.getTime())) return direct;

  const raw = String(version?.versionId || '');
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})(?:-(\d{2}))?$/);
  if (!match) return null;

  const [, y, m, d, hh, mm, ss = '00'] = match;
  const parsed = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatVersionLabelFromDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const month = MONTH_NAMES[value.getMonth()] || '';
  const day = value.getDate();
  const hour = String(value.getHours()).padStart(2, '0');
  const minute = String(value.getMinutes()).padStart(2, '0');
  return `${month} ${day} ${hour}:${minute}`;
};

export const getVersionDisplay = (version) => {
  const parsedDate = parseVersionDate(version);
  const label = formatVersionLabelFromDate(parsedDate) || String(version?.versionId || 'Version');
  return {
    parsedDate,
    label,
    timestamp: parsedDate ? parsedDate.getTime() : 0,
    year: parsedDate ? String(parsedDate.getFullYear()) : null,
    monthIndex: parsedDate ? parsedDate.getMonth() : null,
    monthName: parsedDate ? MONTH_NAMES[parsedDate.getMonth()] : null
  };
};

export const buildVersionDocId = (docIdParam, versionId) => {
  const normalizedDocId = String(docIdParam || '');
  const normalizedVersionId = String(versionId || '').replace(/[^a-zA-Z0-9-_]/g, '-');
  return `version-${normalizedDocId}-${normalizedVersionId}`;
};

export const createContentSignature = (content) => {
  try {
    const normalizedContent = removeChunkAttributes(content ?? null);
    const normalized = JSON.stringify(normalizedContent ?? null);
    return normalized ? fnv1aHash(normalized) : null;
  } catch {
    return fnv1aHash(String(content ?? ''));
  }
};

export const computeCharOffset = (previousContent, nextContent) => {
  const oldNorm = removeChunkAttributes(previousContent ?? null) || null;
  const newNorm = removeChunkAttributes(nextContent ?? null) || null;
  return computeContentOffset(oldNorm, newNorm);
};

const logStorageDriftAudit = async ({
  projectId,
  docId,
  context = 'audit',
  r2removingImages = 0,
  r2removingVersions = 0,
  getVersions: getVersionsFn
} = {}) => {
  if (!projectId || !docId || typeof getVersionsFn !== 'function' || !(isDebugEnabled && isDebugEnabled())) return;

  try {
    const [workerVersions, dbImages, dbVersions, r2ImageKeys] = await Promise.all([
      getVersionsFn({ projectId, docId }).catch(() => []),
      apiGetImagesForDoc(docId).catch(() => []),
      apiGetVersionsForDoc(docId).catch(() => []),
      getDocImageKeysFromR2({ projectId, docId }).catch(() => [])
    ]);

    const dbVersionIds = new Set((dbVersions || []).map(v => String(v?.id || '')).filter(Boolean));
    const r2VersionIds = new Set((workerVersions || []).map(v => String(v?.versionId || '')).filter(Boolean));
    const dbImageKeys = new Set((dbImages || []).map(i => String(i?.key || '')).filter(Boolean));
    const r2Images = new Set((r2ImageKeys || []).map(k => String(k || '')).filter(Boolean));

    const onlyInR2Versions = Array.from(r2VersionIds).filter(id => !dbVersionIds.has(id));
    const onlyInDbVersions = Array.from(dbVersionIds).filter(id => !r2VersionIds.has(id));
    const onlyInR2Images = Array.from(r2Images).filter(key => !dbImageKeys.has(key));
    const onlyInDbImages = Array.from(dbImageKeys).filter(key => !r2Images.has(key));

    const hasDrift =
      onlyInR2Versions.length > 0 ||
      onlyInDbVersions.length > 0 ||
      onlyInR2Images.length > 0 ||
      onlyInDbImages.length > 0;

    if (!hasDrift) {
      return;
    }

    debug('storage-audit', { type: 'warn' }, `drift detected context=${context} docId=${docId} r2removingImages=${Number(r2removingImages || 0)} r2removingVersions=${Number(r2removingVersions || 0)} versions=${r2VersionIds.size} (r2) / ${dbVersionIds.size} (db) images=${r2Images.size} (r2) / ${dbImageKeys.size} (db)`);

    debug('storage-audit', { type: 'warn' }, {
      context,
      docId,
      versionsR2: Array.from(r2VersionIds),
      versionsDb: Array.from(dbVersionIds),
      imagesR2: Array.from(r2Images),
      imagesDb: (dbImages || []).map(img => String(img?.url || img?.key || '')).filter(Boolean),
      drift: {
        onlyInR2Versions,
        onlyInDbVersions,
        onlyInR2Images,
        onlyInDbImages
      }
    });
  } catch (err) {
    debug('storage-audit failed', { type: 'warn' }, err);
  }
};

export const getVersions = async ({ projectId, docId, signal } = {}) => {
  if (!projectId || !docId) {
    return [];
  }

  const url = buildVersionRequestUrl({ projectId, docId });
  const response = await requestWorker(url, { method: 'GET', signal });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to load versions (${response.status})${body ? ` - ${body}` : ''}`);
  }

  const payload = await response.json();
  const versions = Array.isArray(payload?.data) ? payload.data : [];
  const mapped = versions
    .map(entry => ({
      versionId: entry?.version?.id || entry?.versionId || entry?.version_id,
      savedAt: entry?.version?.saved_at || entry?.savedAt || entry?.saved_at || entry?.metadata?.saved_at || null,
      metadata: entry?.metadata || entry?.customMetadata || {},
      size: entry?.size ?? null,
      key: entry?.key
    }))
    .filter(v => Boolean(v.versionId));

  // Backfill DB `versions` table from canonical R2 list (idempotent upsert).
  // This ensures legacy versions that already exist in R2 are represented in SQL.
  try {
    if (mapped.length > 0) {
      await Promise.allSettled(mapped.map(v => apiBackfillVersionIfMissing({
        id: v.versionId,
        docId,
        savedAt: v.savedAt || null,
        metadata: v.metadata || {}
      })));
    }
  } catch (err) {
    debug('[getVersions] backfill failed', { type: 'warn' }, err);
  }

  // Enrich worker versions with canonical DB metadata (name, protect, trigger, signatures)
  try {
    const rows = await apiGetVersionsForDoc(docId).catch(() => []);
    const rowById = new Map((rows || []).map(row => [String(row.id), row]));
    return mapped.map(version => {
      const row = rowById.get(String(version.versionId));
      if (!row) return version;
      return {
        ...version,
        name: row.name || null,
        protected: Boolean(row.protect ?? row.dont_delete),
        trigger: row.trigger || version?.metadata?.trigger || null,
        contentSignature: row.content_signature || null,
        contentOffset: Number.isFinite(Number(row.content_offset)) ? Number(row.content_offset) : null,
        metadata: {
          ...(version.metadata || {}),
          ...(row.metadata || {})
        }
      };
    });
  } catch (err) {
    return mapped;
  }
};

export const renameVersion = async ({ versionId, name }) => {
  if (!versionId) throw new Error('renameVersion requires versionId');
  return apiRenameVersion(versionId, name);
};

export const setVersionProtect = async ({ versionId, protect }) => {
  if (!versionId) throw new Error('setVersionProtect requires versionId');
  return apiSetVersionProtect(versionId, Boolean(protect));
};

export const pruneVersionsNow = async ({ projectId, docId, maxVersions = MAX_DOC_VERSIONS } = {}) => {
  if (!projectId || !docId || !(maxVersions > 0)) return { pruned: 0 };

  const versions = await getVersions({ projectId, docId });
  const protectedIds = new Set(
    await apiGetProtectedVersionIds(docId).catch(() => [])
  );

  const sorted = [...versions].sort((left, right) => {
    const leftTime = left?.savedAt ? new Date(left.savedAt).getTime() : 0;
    const rightTime = right?.savedAt ? new Date(right.savedAt).getTime() : 0;
    if (rightTime !== leftTime) return rightTime - leftTime;
    return String(right?.versionId || '').localeCompare(String(left?.versionId || ''));
  });

  const prunableOnly = sorted.filter(item => !protectedIds.has(String(item?.versionId || '')));
  const toDelete = prunableOnly.slice(maxVersions);

  let pruned = 0;
  for (const item of toDelete) {
    if (!item?.versionId) continue;
    try {
      await deleteVersion({
        projectId,
        docId,
        versionId: item.versionId,
        skipImageReconcile: true,
        skipStorageAudit: true
      });
      pruned += 1;
    } catch {
      // best-effort prune
    }
  }

  return { pruned };
};

export const getVersionContent = async ({ projectId, docId, versionId } = {}) => {
  if (!projectId || !docId || !versionId) {
    throw new Error('getVersionContent requires projectId, docId, and versionId');
  }

  const url = buildVersionRequestUrl({ projectId, docId, versionId });
  const response = await requestWorker(url, { method: 'GET' });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to load version content (${response.status})${body ? ` - ${body}` : ''}`);
  }

  const payload = await response.json();
  const data = payload?.data || {};
  const versionSource = data?.version && typeof data.version === 'object' ? data.version : {};
  const documentSource =
    (data?.document && typeof data.document === 'object')
      ? data.document
      : ((data?.doc && typeof data.doc === 'object') ? data.doc : data);

  const { content, classification } = normalizeToEditorContent({
    rawContent: documentSource?.content,
    rawContentJson: documentSource?.content_json
  });

  return {
    doc: {
      content,
      layout: documentSource.layout ?? null,
      name: documentSource.name ?? documentSource.title ?? null,
      updated_at: documentSource.updated_at ?? documentSource.saved_at ?? null
    },
    contentSource: classification,
    metadata: data.metadata || data.customMetadata || {},
    stats: (data.stats && typeof data.stats === 'object')
      ? {
          words: Number(data.stats.words || 0),
          chars: Number(data.stats.chars || 0),
          nodes: Number(data.stats.nodes || 0),
          images: Number(data.stats.images || 0)
        }
      : null,
    versionId: versionSource.id || data.version_id || data.versionId || versionId,
    savedAt: versionSource.saved_at || data.saved_at || data.savedAt || data.metadata?.saved_at || null
  };
};

export const deleteVersion = async ({
  projectId,
  docId,
  versionId,
  skipImageReconcile = false,
  skipStorageAudit = false
} = {}) => {
  if (!projectId || !docId || !versionId) {
    throw new Error('deleteVersion requires projectId, docId, and versionId');
  }

  const url = buildVersionRequestUrl({ projectId, docId, versionId });
  const response = await requestWorker(url, { method: 'DELETE' });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to delete version (${response.status})${body ? ` - ${body}` : ''}`);
  }

  // Keep DB versions table in sync (best-effort)
  try {
    await apiDeleteVersionRow(versionId);
  } catch (err) {
    debug('[deleteVersion] failed to delete DB row', { type: 'warn' }, err);
  }

  if (!skipImageReconcile) {
    // Reconcile orphan images after manual version delete.
    try {
      const reconciled = await reconcileOrphanImages({
        projectId,
        docId,
        keepImageUrls: [],
        getVersions
      });

      if (!skipStorageAudit) {
        await logStorageDriftAudit({
          projectId,
          docId,
          context: 'deleteVersion',
          r2removingVersions: 1,
          r2removingImages: Number(reconciled?.prunedImages || 0),
          getVersions
        });
      }
    } catch (err) {
      debug('[deleteVersion] orphan image reconcile failed', { type: 'warn' }, err);
    }
  }

  return response.json();
};

export const createVersion = async ({
  projectId,
  docId,
  snapshot,
  maxVersions = MAX_DOC_VERSIONS,
  trigger = 'manual',
  signature = null,
  contentOffset = null,
  // testing/config overrides (if undefined the values from `src/config.js` are used):
  // `purgeRetentionMs` should be milliseconds (use `config.time()` helper)
  purgeRetentionMs,
} = {}) => {

  // Resolve config-defaults when caller didn't provide overrides
  if (purgeRetentionMs === undefined || purgeRetentionMs === null) {
    purgeRetentionMs = (config && config.VERSIONING && config.VERSIONING.PURGE_RETENTION_MS) || 0;
  }

  if (!projectId || !docId) {
    throw new Error('createVersion requires projectId and docId');
  }
  if (!snapshot) {
    throw new Error('createVersion requires a snapshot payload');
  }

  const versionId = createVersionIdUtc();
  const url = buildVersionRequestUrl({ projectId, docId, versionId });
  const savedAt = new Date().toISOString();
  const effectiveSignature = signature || createContentSignature(
    snapshot?.document?.content ?? snapshot?.content ?? snapshot?.doc?.content ?? null
  );
  const canonicalPayload = createVersionContent({
    projectId,
    docId,
    snapshot,
    versionId,
    savedAt,
    trigger,
    signature: effectiveSignature,
    contentOffset
  });

  const computedStats = computeDocumentStats(canonicalPayload?.document?.content || null);
  const versionStats = {
    words: Number(computedStats?.words || 0),
    chars: Number(computedStats?.chars || 0),
    nodes: Number(computedStats?.nodes || 0),
    images: Number(computedStats?.images || 0)
  };
  canonicalPayload.stats = versionStats;

  const response = await requestWorker(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(canonicalPayload)
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to save version snapshot (${response.status})${body ? ` - ${body}` : ''}`);
  }

  // Persist canonical version metadata in DB so `version_images` FK links are valid.
  // Do this before linking images to avoid 409/FK failures when rows are missing.
  try {
    const versionMetadata = {
      ...(canonicalPayload.metadata || {}),
      document: {
        layout: canonicalPayload?.document?.layout || null,
        updated_at: canonicalPayload?.document?.updated_at || null
      },
      stats: versionStats,
      project_id: Number(projectId),
    };

    await apiUpsertVersion({
      id: versionId,
      docId,
      savedAt,
      trigger,
      contentSignature: effectiveSignature || null,
      contentOffset,
      metadata: versionMetadata
    });
  } catch (err) {
    debug('[createVersion] failed to upsert version row', { type: 'warn' }, err);
  }

  // After successful save to worker, attempt to link any images referenced by this
  // version to the canonical `images` table via `version_images`. This is best-effort
  // and should not block the version save on failure.
  try {
    const imageUrls = collectImageUrlsFromContent(canonicalPayload.document?.content || canonicalPayload.document || {});
    if (imageUrls.length > 0) {
      let existing = await apiGetImagesForDoc(docId).catch(() => []);
      const urlToKey = new Map((existing || []).map(i => [String(i.url || ''), i.key]));
      const knownKeys = new Set((existing || []).map(i => String(i.key || '')).filter(Boolean));
      const docOwnedKeys = new Set((existing || []).map(i => String(i.key || '')).filter(Boolean));
      const keys = [];
      for (const u of imageUrls) {
        let key = urlToKey.get(u);
        if (!key) {
          const extractedKey = extractImageKeyFromUrl(u);
          if (extractedKey) {
            const existingByKey = await apiGetImageByKey(extractedKey).catch(() => null);
            if (existingByKey?.key) {
              knownKeys.add(String(existingByKey.key));
              if (String(existingByKey.doc_id || '') === String(docId)) {
                key = existingByKey.key;
                docOwnedKeys.add(String(existingByKey.key));
              }
              if (existingByKey.url) {
                urlToKey.set(String(existingByKey.url), existingByKey.key);
              } else {
                urlToKey.set(String(u), existingByKey.key);
              }
            }

            // self-heal missing image DB rows if upload DB insert failed previously
            if (!key && !knownKeys.has(extractedKey)) {
              await apiCreateImage({ docId, key: extractedKey, url: u, uploadedBy: null, metadata: {} }).catch(() => null);
              const refreshed = await apiGetImagesForDoc(docId).catch(() => []);
              if (Array.isArray(refreshed) && refreshed.length > 0) {
                existing = refreshed;
              }
              const row = (existing || []).find(i => String(i.key || '') === String(extractedKey));
              if (row?.url) urlToKey.set(String(row.url), row.key);
              if (row?.key) {
                knownKeys.add(String(row.key));
                docOwnedKeys.add(String(row.key));
              }
            }
            if (docOwnedKeys.has(extractedKey)) key = extractedKey;
          }
        }
        if (key && docOwnedKeys.has(String(key))) {
          keys.push(key);
        }
      }
      if (keys.length > 0) {
        await apiLinkImagesToVersion(versionId, keys);
      }
    }
  } catch (err) {
    debug('[createVersion] image link failed', { type: 'warn' }, err);
  }

  let pruned = 0;
  if (maxVersions > 0) {
    const result = await pruneVersionsNow({ projectId, docId, maxVersions });
    pruned = Number(result?.pruned || 0);
  }

  let prunedImages = 0;

  // After pruning old versions, reconcile orphaned images against remaining
  // versions + current doc content; purge from both R2 and DB.
  try {
    const retentionMs = Number(purgeRetentionMs) || 0;
    const imageUrlsInDoc = collectImageUrlsFromContent(canonicalPayload.document?.content || canonicalPayload.document || {});
    const reconciled = await reconcileOrphanImages({
      projectId,
      docId,
      keepImageUrls: imageUrlsInDoc,
      purgeRetentionMs: retentionMs,
      getVersions
    });
    prunedImages = Number(reconciled?.prunedImages || 0);
  } catch (err) {
    // swallow global purge errors - variant is best-effort
  }

  return {
    versionId,
    pruned,
    prunedImages
  };
};

export {
  getWorkerBaseUrl,
  getWorkerSessionToken,
  __setWorkerSessionTokenProvider,
  __setRequestWorker
} from '../api/r2';

